// レビュー依頼の設定（文面テンプレート・送信タイミングと文面の設定）
// 対応ルーター: backend/routers/ugc_review_form.py, backend/routers/ugc_review_schedule.py

export const UGCReviewSetupApiPath = {
  LIST_TEMPLATES: '/api/sites/{site_id}/ugc/review-templates',
  CREATE_TEMPLATE: '/api/sites/{site_id}/ugc/review-templates',
  UPDATE_TEMPLATE: '/api/sites/{site_id}/ugc/review-templates/{template_id}',
  DELETE_TEMPLATE: '/api/sites/{site_id}/ugc/review-templates/{template_id}',
  LIST_SCHEDULE_RULES: '/api/sites/{site_id}/ugc/review-schedule/rules',
  UPDATE_SCHEDULE_RULE: '/api/sites/{site_id}/ugc/review-schedule/rules/{rule_id}',
  CREATE_SCHEDULE_RULE: '/api/sites/{site_id}/ugc/review-schedule/rules',
  DELETE_SCHEDULE_RULE: '/api/sites/{site_id}/ugc/review-schedule/rules/{rule_id}',
  LIST_RULE_BRANDS: '/api/sites/{site_id}/ugc/review-schedule/brands',
  LIST_RULE_CATEGORIES: '/api/sites/{site_id}/ugc/review-schedule/categories',
  LIST_RULE_PRICE_RANGES: '/api/sites/{site_id}/ugc/review-schedule/price-ranges?n_clusters=5',
  LIST_REVIEW_FIELDS: '/api/sites/{site_id}/ugc/review-fields',
  CREATE_REVIEW_FIELD: '/api/sites/{site_id}/ugc/review-fields',
  DELETE_REVIEW_FIELD: '/api/sites/{site_id}/ugc/review-fields/{field_id}'
} as const

/** 本文で使える差し込み。画面に一覧を出すのでここを唯一の定義とする */
export const TEMPLATE_VARIABLES = [
  { key: '{customer_name}', label: 'お客様のお名前' },
  { key: '{product_name}', label: '商品名' },
  { key: '{brand_name}', label: 'ブランド名' },
  { key: '{review_url}', label: 'レビュー投稿ページのURL' }
] as const

export interface ListTemplatesVars {
  site_id: number
}

export interface ReviewTemplate {
  id: number
  channel: string
  template_name: string
  subject: string | null
  body: string
  is_active: boolean
  created_at: string | null
}

export interface ListTemplatesResponse {
  templates: ReviewTemplate[]
}

export interface CreateTemplateVars {
  site_id: number
  channel: string
  template_name: string
  subject?: string | null
  body: string
  is_active?: boolean
}

export interface UpdateTemplateVars {
  site_id: number
  template_id: number
  template_name?: string
  subject?: string | null
  body?: string
  is_active?: boolean
}

export interface DeleteTemplateVars {
  site_id: number
  template_id: number
}

/** フォローアップを送る相手 */
export type FollowupTargetFilter = 'all' | 'opened' | 'clicked' | 'not_opened'

/** 条件の軸。商品・顧客・注文の3種類 */
export type RuleAxis =
  | 'product_id'
  | 'category'
  | 'brand'
  | 'product_tag'
  | 'product_icon'
  | 'price_range'
  | 'purchase_count'
  | 'has_reviewed'
  | 'order_amount'
  | 'days_since_shipped'

export type RuleOp = 'in' | 'not_in' | 'eq' | 'gte' | 'lte' | 'between' | 'is_true' | 'is_false'

/** 1つの条件。ルール内の条件はすべて満たしたときだけ当たる（AND） */
export interface RuleCondition {
  axis: RuleAxis
  op: RuleOp
  /** in / not_in は配列、between は {min,max}、それ以外は単一値。is_true/is_false は不要 */
  value?: unknown
}

export interface ScheduleRule {
  id: number
  /** 空配列は「どの注文にも当たる」＝既定のルール */
  conditions: RuleCondition[]
  rule_label: string | null
  enabled: boolean
  initial_delay_days: number
  delay_hours: number
  followup1_delay_days: number
  followup2_delay_days: number
  initial_template_id: number | null
  followup_template_id: number | null
  followup_target_filter: FollowupTargetFilter
  priority: number
}

export interface ListScheduleRulesVars {
  site_id: number
}

export interface ListScheduleRulesResponse {
  rules: ScheduleRule[]
}

export interface UpdateScheduleRuleVars {
  site_id: number
  rule_id: number
  conditions?: RuleCondition[]
  enabled?: boolean
  initial_delay_days?: number
  delay_hours?: number
  followup1_delay_days?: number
  followup2_delay_days?: number
  initial_template_id?: number | null
  followup_template_id?: number | null
  followup_target_filter?: FollowupTargetFilter
}

/** 投稿フォームのカスタム項目（肌タイプ・サイズ感など） */
export interface ReviewField {
  id: number
  field_name: string
  field_type: 'select' | 'text' | 'number'
  options: string[]
  category_filter: string | null
  is_required: boolean
  display_order: number
}

export interface ListReviewFieldsVars {
  site_id: number
}

export interface ListReviewFieldsResponse {
  fields: ReviewField[]
}

export interface CreateReviewFieldVars {
  site_id: number
  field_name: string
  field_type: 'select' | 'text' | 'number'
  options?: string[] | null
  category_filter?: string | null
  is_required?: boolean
  display_order?: number
}

export interface DeleteReviewFieldVars {
  site_id: number
  field_id: number
}

/** ルールの対象にできる種類（default はサイト全体で常に1つ・削除不可） */
export type ScheduleRuleType =
  | 'default'
  | 'brand'
  | 'category'
  | 'product_group'
  | 'price_range'

export interface CreateScheduleRuleVars {
  site_id: number
  conditions?: RuleCondition[]
  rule_label?: string | null
  enabled?: boolean
  initial_delay_days?: number
  followup1_delay_days?: number
  followup2_delay_days?: number
}

export interface DeleteScheduleRuleVars {
  site_id: number
  rule_id: number
}

export interface RuleBrandsResponse {
  brands: { brand_id: string; brand_name: string }[]
}

export interface RuleCategoriesResponse {
  categories: {
    category_id: string
    category_name: string
    parent_category_id: string | null
    depth: number
  }[]
}

export interface RulePriceRangesResponse {
  ranges: { key: string; min: number; max: number; count: number; label: string }[]
}
